import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Download, Shield, FileText, Code } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

export default function InstallPage() {
  const { profile } = useAuth();

  const downloadAgent = () => {
    const link = document.createElement('a');
    link.href = '/ServSpect-Agent-Setup.exe';
    link.download = 'ServSpect-Agent-Setup.exe';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: "Download Started",
      description: "ServSpect Agent installer is downloading.",
    });
  };

  const downloadTestScript = () => {
    const link = document.createElement('a');
    link.href = '/windows-metrics-collector.ps1';
    link.download = 'windows-metrics-collector.ps1';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: "Download Started", 
      description: "PowerShell test script is downloading.",
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Windows Agent Installation</h1>
        <p className="text-muted-foreground">
          Download and install monitoring components for your Windows systems
        </p>
      </div>

      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          <strong>Tenant:</strong> {profile?.tenants?.name || 'Loading...'}
          <br />
          <strong>Your Tenant ID:</strong> {profile?.tenant_id || 'Please login to see your tenant ID'}
          <br />
          You'll need your Tenant ID during the agent installation process.
        </AlertDescription>
      </Alert>

      {/* Prominent Tenant ID Card */}
      <Card className="border-2 border-primary bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-primary">
            <Shield className="h-5 w-5" />
            Your Configuration Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-background p-4 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-lg">Tenant ID</h4>
                  <p className="text-sm text-muted-foreground">Copy this ID when the script asks for it</p>
                </div>
                <div className="text-right">
                  <code className="bg-muted px-3 py-2 rounded text-lg font-mono">
                    {profile?.tenant_id || 'Please login to see your tenant ID'}
                  </code>
                </div>
              </div>
            </div>
            <div className="bg-background p-4 rounded-lg border">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">Organization</h4>
                  <p className="text-sm text-muted-foreground">Your dashboard account</p>
                </div>
                <div className="text-right">
                  <span className="font-medium">{profile?.tenants?.name || 'Loading...'}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Production Agent */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                ServSpect Agent for Windows
              </CardTitle>
              <CardDescription>
                Pre-built monitoring agent with easy GUI installer
              </CardDescription>
            </div>
            <Button onClick={downloadAgent} size="lg">
              <Download className="h-4 w-4 mr-2" />
              Download Agent
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">1. Download & Run</h4>
              <p className="text-sm text-muted-foreground">
                Download the setup file and run as administrator
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-sm">2. Configure</h4>
              <p className="text-sm text-muted-foreground">
                Enter your tenant ID and configure monitoring settings
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-sm">3. Monitor</h4>
              <p className="text-sm text-muted-foreground">
                Agent starts automatically and data appears in dashboard
              </p>
            </div>
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-medium mb-2">What the agent monitors:</h4>
            <div className="grid gap-2 md:grid-cols-2">
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• <strong>Core Performance:</strong> CPU, Memory, Disk, Network</li>
                <li>• <strong>Processes & Services:</strong> Top processes, Windows services</li>
                <li>• <strong>Hardware Health:</strong> Temperatures, SMART data, battery</li>
                <li>• <strong>OS Configuration:</strong> Updates, BitLocker, licenses</li>
              </ul>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• <strong>Security Events:</strong> Logins, failures, PowerShell</li>
                <li>• <strong>Network Exposure:</strong> Open ports, RDP, WinRM</li>
                <li>• <strong>Asset Inventory:</strong> Hardware info, installed software</li>
                <li>• <strong>Real-time Monitoring:</strong> 15-30 second intervals</li>
              </ul>
            </div>
          </div>

          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              The agent sends data securely via HTTPS to your dashboard. 
              No external dependencies required.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Test Script */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                PowerShell Test Script
              </CardTitle>
              <CardDescription>
                Comprehensive metrics collector for testing and development
              </CardDescription>
            </div>
            <Button onClick={downloadTestScript} variant="outline" size="lg">
              <FileText className="h-4 w-4 mr-2" />
              Download Script
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted p-4 rounded-lg">
            <h4 className="font-medium mb-2">PowerShell Script Features:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Interactive Setup Wizard:</strong> Guides you through first-time configuration</li>
              <li>• <strong>Automatic Server Registration:</strong> No manual server setup required</li>
              <li>• <strong>Configuration Storage:</strong> Saves your settings locally for future runs</li>
              <li>• <strong>Complete Metric Collection:</strong> All 7 Windows metric categories</li>
              <li>• <strong>Continuous Monitoring:</strong> Runs indefinitely with smart error handling</li>
              <li>• <strong>User-Friendly Interface:</strong> Color-coded output and clear status messages</li>
            </ul>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">First Run (Setup):</h4>
              <code className="text-xs bg-muted p-2 rounded block">
                .\windows-metrics-collector.ps1
              </code>
              <p className="text-xs text-muted-foreground">Will ask for your Tenant ID (shown above) and start monitoring</p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Quick Start with Tenant ID:</h4>
              <code className="text-xs bg-muted p-2 rounded block"> 
                .\windows-metrics-collector.ps1 -TenantId "your-tenant-id"
              </code>
              <p className="text-xs text-muted-foreground">Direct start with your Tenant ID from above</p>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="font-medium mb-2 text-blue-900 dark:text-blue-100">🚀 Super Easy Setup:</h4>
            <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>1. Download and run the script as Administrator</li>
              <li>2. Copy your Tenant ID from the box above when prompted</li>
              <li>3. Script automatically registers your server and starts monitoring</li>
              <li>4. View real-time data in your Metrics dashboard!</li>
            </ol>
          </div>

          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              <strong>Note:</strong> The script only needs your Tenant ID (shown above). All other connection details are pre-configured. Use <code>-ResetConfig</code> to change your Tenant ID later.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}